import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Kbd,
  Loader,
  Progress,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import type { DraftQuestion } from '@prepzio/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDrafts, useReviewBatch, useReviewStats } from '../hooks/use-content';
import { useEnumLabel, usePlural, useReasonLabel, useT } from '../i18n';
import type { ReviewDecision } from '../lib/api';
import { errorMessage } from '../lib/ui';

/**
 * PRE-262 — the review queue.
 *
 * Human review is the bottleneck: at ~15,000 (skill × difficulty × type) cells
 * the generator is not what limits the bank, a person reading is. The old
 * screen reviewed one draft per page load, which is fine for a dozen and
 * hopeless for a thousand.
 *
 * So this is a keyboard queue, not a table. One draft at a time, full width,
 * with the reasons on number keys — because the thing being optimised is the
 * interval between "I have read this" and "the next one is on screen", and a
 * mouse round-trip to a dropdown is most of that interval.
 *
 * Decisions are held locally and sent as one batch. Not for the round trips:
 * it means a reviewer never waits on the network between drafts, which is what
 * lets them work at the speed of reading.
 */

/**
 * The reasons, in the order a reviewer meets them.
 *
 * Numbered so the common ones are on the keys a hand is already near. The list
 * is closed on purpose (see the shared schema): at this volume the aggregate is
 * what changes the prompt template, and thirty typed sentences are thirty
 * sentences nobody reads. The note field is there for what the list misses.
 */
const REASONS: { key: string; value: string }[] = [
  { key: '1', value: 'factually_wrong' },
  { key: '2', value: 'ambiguous' },
  { key: '3', value: 'off_skill' },
  { key: '4', value: 'badly_worded' },
  { key: '5', value: 'too_easy' },
  { key: '6', value: 'too_hard' },
  { key: '7', value: 'duplicate' },
  { key: '8', value: 'biased' },
  { key: '9', value: 'other' },
];

export function ReviewQueueScreen() {
  const t = useT();
  const plural = usePlural();
  const label = useEnumLabel();
  const reasonLabel = useReasonLabel();
  const drafts = useDrafts({ status: 'draft' });
  const stats = useReviewStats();
  const batch = useReviewBatch();

  const [index, setIndex] = useState(0);
  const [decisions, setDecisions] = useState<Map<string, ReviewDecision>>(new Map());
  const [note, setNote] = useState('');

  const items = useMemo(() => drafts.data?.items ?? [], [drafts.data]);
  const current: DraftQuestion | undefined = items[index];

  const decide = useCallback(
    (verdict: 'approve' | 'reject', reason?: string) => {
      if (!current) return;
      setDecisions((prev) => {
        const next = new Map(prev);
        next.set(current.id, {
          id: current.id,
          verdict,
          reason,
          note: note.trim() || undefined,
        });
        return next;
      });
      setNote('');
      setIndex((i) => Math.min(i + 1, items.length));
    },
    [current, items.length, note],
  );

  /**
   * The keyboard is the interface.
   *
   * `a` approves, a digit rejects with that reason, `u` steps back — the one
   * that matters most, because a queue you cannot correct is one people slow
   * down to be careful in, which is the opposite of the point. Nothing is sent
   * until the batch is applied, so stepping back is free.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never steal a key from the note field.
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'a') {
        e.preventDefault();
        decide('approve');
        return;
      }
      if (e.key === 'u') {
        e.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
        return;
      }
      const reason = REASONS.find((r) => r.key === e.key);
      if (reason) {
        e.preventDefault();
        decide('reject', reason.value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [decide]);

  if (drafts.isPending) return <Loader />;
  if (drafts.error) return <Alert color="red">{errorMessage(drafts.error)}</Alert>;

  const pending = [...decisions.values()];
  const done = index >= items.length;

  const apply = () => {
    batch.mutate(pending, {
      onSuccess: () => {
        setDecisions(new Map());
        setIndex(0);
        void drafts.refetch();
      },
    });
  };

  return (
    <Stack gap="lg">
      {stats.data ? (
        <Group gap="lg" align="stretch">
          <Card withBorder>
            <Text size="xs" c="dimmed">
              {t('queue.waiting')}
            </Text>
            <Text fw={700}>{stats.data.pendingReview}</Text>
          </Card>
          <Card withBorder>
            <Text size="xs" c="dimmed">
              {t('queue.approvalRate')}
            </Text>
            <Text fw={700}>
              {stats.data.approvalRate == null
                ? '—'
                : `${Math.round(stats.data.approvalRate * 100)}%`}
            </Text>
            {/* The number the blocking threshold is judged by. Very low means
                people are reading drafts the judge should have stopped; very
                high means it is stopping questions we could have had. */}
            <Text size="xs" c="dimmed">
              {t('queue.approvalRateHint')}
            </Text>
          </Card>
          <Card withBorder>
            <Text size="xs" c="dimmed">
              {t('queue.stoppedBefore')}
            </Text>
            <Text fw={700}>{stats.data.judgeBlocked}</Text>
            <Text size="xs" c="dimmed">
              {t('queue.stoppedBeforeHint')}
            </Text>
          </Card>
        </Group>
      ) : null}

      {items.length === 0 ? (
        <Alert color="green">{t('queue.empty')}</Alert>
      ) : (
        <>
          <Group justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              {t('queue.position', {
                index: Math.min(index + 1, items.length),
                total: items.length,
              })}
            </Text>
            <Progress value={(index / items.length) * 100} w={240} />
          </Group>

          {current ? (
            <Card withBorder padding="lg">
              <Stack gap="sm">
                <Group gap="xs">
                  <Badge>{label('difficulty', current.difficulty)}</Badge>
                  <Badge variant="light">{label('type', current.type)}</Badge>
                  {current.judgeScore != null ? (
                    <Badge color={current.judgeScore >= 0.6 ? 'green' : 'yellow'}>
                      {t('queue.judgePercent', { percent: Math.round(current.judgeScore * 100) })}
                    </Badge>
                  ) : null}
                  {current.duplicateOfId ? (
                    <Badge color="orange">{t('queue.nearDuplicate')}</Badge>
                  ) : null}
                </Group>

                <Text fw={600}>{current.prompt}</Text>

                {current.options?.length ? (
                  <Stack gap={4}>
                    {current.options.map((o) => (
                      <Text key={o.id} size="sm" c={o.correct ? 'green' : undefined}>
                        {o.correct ? '✓ ' : '· '}
                        {o.text}
                      </Text>
                    ))}
                  </Stack>
                ) : null}

                <Text size="sm" c="dimmed">
                  {t('queue.strongAnswer', { answer: current.exampleStrongAnswer })}
                </Text>

                {current.judgeRationale ? (
                  <Alert color="gray" title={t('queue.judgeSaid')}>
                    {current.judgeRationale}
                  </Alert>
                ) : null}

                <Textarea
                  placeholder={t('queue.notePlaceholder')}
                  value={note}
                  onChange={(e) => setNote(e.currentTarget.value)}
                  autosize
                  minRows={1}
                  maxLength={500}
                />

                <Group gap="xs">
                  <Button color="green" onClick={() => decide('approve')}>
                    {t('queue.approve')} <Kbd ml="xs">a</Kbd>
                  </Button>
                  {REASONS.map((r) => (
                    <Button
                      key={r.value}
                      variant="light"
                      color="red"
                      size="xs"
                      onClick={() => decide('reject', r.value)}
                    >
                      {reasonLabel(r.value)} <Kbd ml={6}>{r.key}</Kbd>
                    </Button>
                  ))}
                </Group>

                <Text size="xs" c="dimmed">
                  {t('queue.keyHelp')}
                </Text>
              </Stack>
            </Card>
          ) : null}

          {done ? <Alert color="blue">{t('queue.end')}</Alert> : null}

          {pending.length > 0 ? (
            <Group>
              <Button loading={batch.isPending} onClick={apply}>
                {plural('queue.apply_one', 'queue.apply', pending.length)}
              </Button>
              <Button variant="subtle" onClick={() => setDecisions(new Map())}>
                {t('queue.discard')}
              </Button>
            </Group>
          ) : null}

          {batch.data && batch.data.skipped > 0 ? (
            <Alert color="yellow" title={t('queue.skipped', { count: batch.data.skipped })}>
              {/* Two admins on one queue is the normal case. The rest of the
                  sitting still landed — this says which ones did not. */}
              {batch.data.results
                .filter((r) => !r.ok)
                .map((r) => (
                  <Text key={r.id} size="sm">
                    <Code>{r.id.slice(0, 8)}</Code> {r.error}
                  </Text>
                ))}
            </Alert>
          ) : null}
        </>
      )}

      {stats.data && stats.data.reasons.length > 0 ? (
        <div>
          <Title order={4}>{t('queue.reasonsTitle')}</Title>
          <Text size="sm" c="dimmed" mb="sm">
            {t('queue.reasonsHint')}
          </Text>
          <Stack gap={4}>
            {stats.data.reasons.map((r) => (
              <Group key={r.reason} gap="sm">
                <Text size="sm" w={220}>
                  {reasonLabel(r.reason)}
                </Text>
                <Progress
                  value={(r.count / stats.data!.reasons[0]!.count) * 100}
                  w={200}
                  color={r.reason === 'judge_blocked' ? 'gray' : 'red'}
                />
                <Text size="sm">{r.count}</Text>
              </Group>
            ))}
          </Stack>
        </div>
      ) : null}
    </Stack>
  );
}
